import * as THREE from "three";
import PhysicsService from "../physics/physics.service"
import { checkIfCameraIsOutside } from "./camera.utils";
import { getConfiguredRuntimeCameraHeight, getPersistedCameraHeight } from "../../../modals/render.settings.camera";

import AppData from "../../app.data";
const appData = new AppData()
/** Distance threshold to hide remove icons when camera is too close */
const DISTANCE_FROM_CAMERA_TO_HIDE_REMOVES = 6;
const VR_MOVE_SPEED = 0.15;

/**
 * Manages camera functionality in the 3D environment
 * Handles camera movement, rotation, physics integration, and position persistence
 * Controls editor-specific features like remove icon visibility
 */
class CameraService {
  /** Canvas dimensions and position */
  canvasRect = null as DOMRect | null;
  /** Camera movement speed multiplier */
  camSpeed = 1 as any;
  /** Last recorded mouse position */
  lastMouse: any;
  /** Physics service for collision detection and movement */
  physicsService: PhysicsService;
  /** Collection of remove icons for editor mode */
  removeIcons: any = []

  /**
   * Initializes the camera service with canvas element
   * Sets up perspective camera with initial parameters
   * @param canvas HTML canvas element for viewport dimensions
   */
  constructor(canvas: HTMLCanvasElement) {
    this.canvasRect = canvas.getBoundingClientRect();
    appData.camera = new THREE.PerspectiveCamera(
      60,
      this.canvasRect.width / this.canvasRect.height,
      0.1,
      200
    );
    appData.camera.rotation.order = 'YXZ';
  }

  /**
   * Associates physics service with camera for movement and collisions
   * @param physicsService Physics service instance
   */
  public addPhysics(physicsService: PhysicsService) {
    this.physicsService = physicsService;
  }

  /**
   * Returns the current camera instance
   * @returns THREE.PerspectiveCamera instance
   */
  public getCamera() {
    return appData.camera
  }

  /**
   * Sets camera position and rotation based on gallery data
   * Updates physics collider position to match camera
   * Skips rotation setting in VR mode to preserve head tracking
   */
  public setCameraTransforms() {
    let cameraPosition = checkIfCameraIsOutside(appData.galleryData.data.camera.position)

    if (appData.galleryData.spaceType === "mom" || appData.galleryData.spaceType === "sellermom") {
      cameraPosition = new THREE.Vector3(3, 1.4, 6.5)
    }
    const cameraHeight = getConfiguredRuntimeCameraHeight(appData)
    this.physicsService.playerCollider.end.x = cameraPosition.x
    this.physicsService.playerCollider.start.x = cameraPosition.x
    this.physicsService.playerCollider.start.y = cameraHeight
    this.physicsService.playerCollider.end.y = cameraHeight
    this.physicsService.playerCollider.end.z = cameraPosition.z
    this.physicsService.playerCollider.start.z = cameraPosition.z

    // Skip camera rotation in VR mode to preserve head tracking
    if (appData.renderService && appData.renderService.renderer && appData.renderService.renderer.xr.isPresenting) {
      return;
    }

    appData.camera.rotation.x = appData.galleryData.data.camera.rotation.x;
    appData.camera.rotation.y = appData.galleryData.data.camera.rotation.y;
  }

  /**
   * Saves current camera position and rotation to localStorage
   * Used for persisting camera state between sessions
   */
  public saveCameraPositions() {
    const key = "CameraPosition";
    const persistedCameraHeight = getPersistedCameraHeight(appData);

    const capsulePosition = {
      x: this.physicsService.playerCollider.end.x,
      y: persistedCameraHeight,
      z: this.physicsService.playerCollider.end.z
    };

    const cameraRotation = {
      x: appData.camera.rotation.x,
      y: appData.camera.rotation.y,
    };

    const positions = {
      capsulePosition,
      cameraRotation,
    };

    localStorage.setItem(key, JSON.stringify(positions));
  }

  /**
   * Loads camera position and rotation from localStorage
   * Restores camera state from previous session
   * Skips rotation setting in VR mode to preserve head tracking
   */
  public loadCameraPositions() {
    const key = "CameraPosition";
    const storedPositions = localStorage.getItem(key);

    if (storedPositions && this.physicsService && this.physicsService.playerCollider) {
      const { capsulePosition, cameraRotation } = JSON.parse(storedPositions);
      const cameraHeight = getConfiguredRuntimeCameraHeight(appData);

      this.physicsService.playerCollider.end.x = capsulePosition.x
      this.physicsService.playerCollider.start.x = capsulePosition.x
      this.physicsService.playerCollider.end.z = capsulePosition.z
      this.physicsService.playerCollider.start.z = capsulePosition.z
      this.physicsService.playerCollider.start.y = cameraHeight
      this.physicsService.playerCollider.end.y = cameraHeight
      appData.camera.position.y = cameraHeight

      // Skip camera rotation in VR mode to preserve head tracking
      if (!(appData.renderService && appData.renderService.renderer && appData.renderService.renderer.xr.isPresenting)) {
        appData.camera.rotation.x = cameraRotation.x;
        appData.camera.rotation.y = cameraRotation.y;
      }

      // Remove the saved data after loading it
      localStorage.removeItem(key);
    }
  }

  /**
   * Moves camera right/left based on direction
   * Updates physics velocity and handles remove icon visibility
   * In VR mode, moves the VR rig instead of camera
   * @param direction Movement direction (-1 for left, 1 for right)
   */
  public controlMoveRight(direction: number) {
    if (this.isVRMode()) {
      this.controlVRMoveRight(direction);
    } else {
      this.physicsService.playerVelocity.add(this.physicsService.getSideVector().multiplyScalar(direction * 10));
    }
    this.hideRemoveIconsForEditors()
  }

  /**
   * Moves camera forward/backward based on direction
   * Updates physics velocity and handles remove icon visibility
   * In VR mode, moves the VR rig instead of camera
   * @param direction Movement direction (-1 for backward, 1 for forward)
   */
  public controlMoveForward(direction: number) {
    if (this.isVRMode()) {
      this.controlVRMoveForward(direction);
    } else {
      this.physicsService.playerVelocity.add(this.physicsService.getForwardVector().multiplyScalar(direction * 10));
    }
    this.hideRemoveIconsForEditors()
  }

  /**
   * Checks if we're currently in VR mode
   * @returns true if VR is active
   */
  private isVRMode(): boolean {
    return appData.renderService && appData.renderService.renderer && appData.renderService.renderer.xr.isPresenting;
  }

  /**
   * Handles VR-specific right/left movement
   * Moves the VR rig instead of the camera
   * @param direction Movement direction (-1 for left, 1 for right)
   */
  private controlVRMoveRight(direction: number) {
    if (!appData.renderService || !appData.renderService.vrManager.vrRig) return;

    // Get the camera's current world direction for movement relative to head orientation
    const cameraDirection = new THREE.Vector3();
    appData.camera.getWorldDirection(cameraDirection);

    // Calculate right vector (perpendicular to forward direction)
    const rightVector = new THREE.Vector3();
    rightVector.crossVectors(cameraDirection, appData.camera.up).normalize();

    this.moveVRRigWithCollision(rightVector.multiplyScalar(direction * VR_MOVE_SPEED));

  }

  /**
   * Handles VR-specific forward/backward movement
   * Moves the VR rig instead of the camera
   * @param direction Movement direction (-1 for backward, 1 for forward)
   */
  private controlVRMoveForward(direction: number) {
    if (!appData.renderService || !appData.renderService.vrManager.vrRig) return;

    // Get the camera's current world direction for movement relative to head orientation
    const cameraDirection = new THREE.Vector3();
    appData.camera.getWorldDirection(cameraDirection);

    // Remove vertical component for ground-based movement
    cameraDirection.y = 0;
    cameraDirection.normalize();

    this.moveVRRigWithCollision(cameraDirection.multiplyScalar(direction * VR_MOVE_SPEED));

  }

  private moveVRRigWithCollision(worldDelta: THREE.Vector3) {
    const vrRig = appData.renderService?.vrManager?.vrRig;
    if (!vrRig) return;

    const cameraWorldPosition = new THREE.Vector3();
    appData.camera.getWorldPosition(cameraWorldPosition);
    const currentHeadGalleryPosition = this.getVRHeadGalleryPosition(vrRig, cameraWorldPosition);
    const nextHeadGalleryPosition = this.getVRHeadGalleryPosition(
      vrRig,
      cameraWorldPosition.clone().add(worldDelta)
    );
    const galleryDelta = nextHeadGalleryPosition.clone().sub(currentHeadGalleryPosition);

    if (!this.physicsService?.playerCollider || !this.physicsService?.colideSet) {
      this.positionVRRigAtHeadGalleryPosition(vrRig, nextHeadGalleryPosition, cameraWorldPosition);
      return;
    }

    this.physicsService.setPlayerColliderHeadPosition(
      currentHeadGalleryPosition,
      getConfiguredRuntimeCameraHeight(appData)
    );
    const resolvedHeadGalleryPosition = this.physicsService.movePlayerCollider(galleryDelta);
    this.positionVRRigAtHeadGalleryPosition(vrRig, resolvedHeadGalleryPosition, cameraWorldPosition);
  }

  private getVRHeadGalleryPosition(vrRig: THREE.Group, cameraWorldPosition: THREE.Vector3) {
    return vrRig.worldToLocal(cameraWorldPosition.clone());
  }

  private positionVRRigAtHeadGalleryPosition(
    vrRig: THREE.Group,
    headGalleryPosition: THREE.Vector3,
    cameraWorldPosition?: THREE.Vector3
  ) {
    const headsetWorldPosition = cameraWorldPosition || new THREE.Vector3();
    if (!cameraWorldPosition) {
      appData.camera.getWorldPosition(headsetWorldPosition);
    }

    const headWorldOffset = headGalleryPosition.clone().applyQuaternion(vrRig.quaternion);
    vrRig.position.copy(headsetWorldPosition.clone().sub(headWorldOffset));
    vrRig.updateMatrixWorld(true);
  }

  public controlVRSnapTurn(direction: -1 | 1) {
    if (!appData.renderService || !appData.renderService.vrManager.vrRig) return;

    const vrRig = appData.renderService.vrManager.vrRig;
    const cameraWorldPosition = new THREE.Vector3();
    appData.camera.getWorldPosition(cameraWorldPosition);
    const headGalleryPosition = this.getVRHeadGalleryPosition(vrRig, cameraWorldPosition);
    const turnAmount = THREE.MathUtils.degToRad(30) * direction;
    vrRig.rotation.y += turnAmount;
    this.positionVRRigAtHeadGalleryPosition(vrRig, headGalleryPosition, cameraWorldPosition);
  }

  public teleportVRTo(targetGalleryPosition: THREE.Vector3) {
    if (!appData.renderService || !appData.renderService.vrManager.vrRig) {
      return;
    }

    const vrRig = appData.renderService.vrManager.vrRig;
    const cameraWorldPosition = new THREE.Vector3();
    appData.camera.getWorldPosition(cameraWorldPosition);
    const targetHeadPosition = targetGalleryPosition.clone();
    targetHeadPosition.y += getConfiguredRuntimeCameraHeight(appData);

    this.positionVRRigAtHeadGalleryPosition(vrRig, targetHeadPosition, cameraWorldPosition);
    this.physicsService.setPlayerColliderHeadPosition(
      targetHeadPosition,
      getConfiguredRuntimeCameraHeight(appData)
    );
  }

  /**
   * Stores remove icons for editor mode visibility management
   * @param removeIcons Array of remove icon objects
   */
  public collectRemoveIcons(removeIcons: any) {
    this.removeIcons = removeIcons
  }

  /**
   * Updates visibility of remove icons based on camera distance
   * Hides icons when camera is too close
   */
  public hideRemoveIconsForEditors() {
    if (this.removeIcons.length > 0) {
      for (let index = 0; index < this.removeIcons.length; index++) {

        const removeIcon = this.removeIcons[index];
        removeIcon.visible = true
        if (this.physicsService.playerCollider.end.distanceTo(removeIcon.userData.realWorldPosition) < DISTANCE_FROM_CAMERA_TO_HIDE_REMOVES) {
          removeIcon.visible = false
        }

      }
    }
  }

  /**
   * Rotates camera based on mouse movement
   * Skips rotation in VR mode to allow natural head tracking
   * @param movementX Horizontal mouse movement
   * @param movementY Vertical mouse movement
   */
  public controlRotateCamera(movementX: number, movementY: number) {
    // Skip manual rotation in VR mode - let WebXR handle head tracking
    if (appData.renderService && appData.renderService.renderer && appData.renderService.renderer.xr.isPresenting) {
      return;
    }

    appData.camera.rotation.y -= movementX / 500;
    appData.camera.rotation.x -= movementY / 500;
  }

  /**
   * Placeholder for cleanup operations
   * Currently commented out functionality
   */
  public dispose = () => {
    try {
      this.removeIcons = [];
      this.canvasRect = null;
      this.lastMouse = null;
      if (appData.camera?.parent) {
        appData.camera.parent.remove(appData.camera);
      }
    } catch (err) {
      console.log(err);
    }
  };

  /**
   * Cleans up camera resources and nullifies references
   * Removes camera instance and clears dependencies
   */
  public destroy() {
    // Remove the camera and camera from the scene.
    // if (this.spaceLoader && this.spaceLoader.scene) {
    //   this.spaceLoader.scene.remove(this.camera);
    // }

    // // Remove audio listener from the camera.
    // if (
    //   this.spaceLoader &&
    //   this.spaceLoader.soundsLoader &&
    //   this.spaceLoader.soundsLoader.listener
    // ) {
    //   this.camera.remove(this.spaceLoader.soundsLoader.listener);
    // }

    // // Dispose of the camera resources.
    appData.camera = null;

    // // Nullify dependencies to aid in garbage collection.
    this.canvasRect = null;
    this.lastMouse = null;
  }
}

export default CameraService;
